import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { compareApi } from '../api';
import { Badge, Button, EmptyState, Loading, Switch, TextArea } from '../components/common';
import { AppShell } from '../components/layout/AppShell';
import { Sidebar } from '../components/layout/Sidebar';
import { TopBar } from '../components/layout/TopBar';
import { useModel } from '../context/ModelContext';
import { useToast } from '../context/ToastContext';
import { useConversations } from '../hooks/useConversations';
import { useImageUpload } from '../hooks/useImageUpload';
import { formatMetric, formatMs } from '../utils/format';
import './pages.css';

/**
 * Run one image and prompt through several versions and diff the answers.
 *
 * Versions run sequentially with an adapter swap between them — one adapter can be
 * active at a time. That is ~1–2 s of overhead per version, which is why this is a
 * deliberate action with a button rather than something that happens on every send.
 */
export function ComparePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { readyVersions, activeId, inferenceAlive, isVllm } = useModel();
  const conversations = useConversations();
  const upload = useImageUpload({ max: 1 });

  const [selected, setSelected] = useState(() => (activeId ? [activeId] : []));
  const [promptText, setPromptText] = useState('');
  const [onlyDiffs, setOnlyDiffs] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (versionId) =>
    setSelected((current) =>
      current.includes(versionId)
        ? current.filter((id) => id !== versionId)
        : current.length >= 4
          ? current
          : [...current, versionId],
    );

  const run = useCallback(async () => {
    if (selected.length < 2) {
      toast.warn('Pick two versions', 'Comparison needs at least two.');
      return;
    }
    if (!upload.imageIds.length) {
      toast.warn('No image', 'Add the image to compare on.');
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const payload = await compareApi.run({
        version_ids: selected,
        image_ids: upload.imageIds,
        prompt_text: promptText.trim() || undefined,
        mode: promptText.trim() ? 'chat' : 'extraction',
      });
      setResult(payload);
      const failures = payload.results.filter((entry) => entry.error);
      if (failures.length) {
        toast.warn(
          `${failures.length} version${failures.length === 1 ? '' : 's'} failed`,
          failures[0].error,
        );
      }
    } catch (cause) {
      toast.error(cause, { title: 'Comparison failed' });
    } finally {
      setRunning(false);
    }
  }, [promptText, selected, toast, upload.imageIds]);

  const diffRows = result
    ? onlyDiffs
      ? result.diff.filter((row) => row.differs)
      : result.diff
    : [];

  return (
    <AppShell
      sidebar={
        <Sidebar
          conversations={conversations.conversations}
          loading={conversations.loading}
          onNewAnalysis={() => navigate('/')}
          onSelectConversation={(id) => navigate(`/c/${id}`)}
          onDeleteConversation={conversations.remove}
        />
      }
    >
      <main className="page">
        <TopBar title="Compare versions" showVersion={false} />
        <div className="page__body">
          <div className="page__head">
            <div className="page__subtitle">
              Same image, same prompt, every selected version — with the disagreements highlighted.
              Versions run one after another because only one LoRA adapter can be active at a time.
            </div>
          </div>

          <div className="compare-setup">
            <section className="card card--pad">
              <div className="section-label" style={{ marginBottom: 8 }}>
                Image
              </div>
              {upload.items.length ? (
                <div className="row" style={{ alignItems: 'flex-start' }}>
                  <img
                    src={upload.items[0].previewUrl}
                    alt={upload.items[0].filename}
                    style={{
                      width: 220,
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--line)',
                    }}
                  />
                  <div className="col" style={{ gap: 6 }}>
                    <div className="small">{upload.items[0].filename}</div>
                    <Button size="sm" variant="ghost" onClick={upload.clear}>
                      Replace
                    </Button>
                  </div>
                </div>
              ) : (
                <label className="drop card--pad" style={{ cursor: 'pointer', minHeight: 130 }}>
                  <div className="drop__title">Drop or choose one image</div>
                  <div className="drop__hint">All versions are scored on this frame</div>
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={(event) => {
                      upload.addFiles(event.target.files);
                      event.target.value = '';
                    }}
                  />
                </label>
              )}

              <div className="section-label" style={{ margin: '14px 0 8px' }}>
                Prompt (optional)
              </div>
              <TextArea
                value={promptText}
                onChange={(event) => setPromptText(event.target.value)}
                placeholder="Leave empty to use each version's generated extraction prompt — the training-parity path. Type a question to compare free-form answers instead."
                style={{ minHeight: 72 }}
              />
            </section>

            <section className="card card--pad col" style={{ gap: 10 }}>
              <div className="section-label">Versions ({selected.length}/4)</div>
              {readyVersions.length === 0 ? (
                <div className="small muted">No ready versions. Add metadata in Settings.</div>
              ) : (
                readyVersions.map((version) => (
                  <Switch
                    key={version.id}
                    checked={selected.includes(version.id)}
                    onChange={() => toggle(version.id)}
                    label={version.label || version.id}
                    disabled={!selected.includes(version.id) && selected.length >= 4}
                  />
                ))
              )}
              <Button
                variant="primary"
                onClick={run}
                loading={running}
                disabled={selected.length < 2 || !upload.imageIds.length || !inferenceAlive}
                block
              >
                Run comparison
              </Button>
              {selected.length >= 2 && (
                <div className="xs subtle">
                  {isVllm
                    ? 'The versions run side by side on vLLM; each needs its base model loaded.'
                    : `≈ ${selected.length} × (inference + ~2 s adapter swap)`}
                </div>
              )}
            </section>
          </div>

          {running && <Loading label="Running each version in turn…" large />}

          {result && !running && (
            <>
              <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                <strong>
                  {result.differing_count} of {result.compared_attributes} attributes differ
                </strong>
                <span className="small muted">· total {formatMs(result.elapsed_ms)}</span>
                <div className="grow" />
                <Switch checked={onlyDiffs} onChange={setOnlyDiffs} label="Only differences" />
              </div>

              <div className="compare-cols" style={{ marginBottom: 16 }}>
                {result.results.map((entry) => (
                  <div key={entry.version.id} className="card">
                    <div className="compare-col__head">
                      <strong>{entry.version.label || entry.version.id}</strong>
                      {entry.error ? (
                        <Badge tone="danger">failed</Badge>
                      ) : (
                        <>
                          <Badge tone="ok">{entry.attributes?.counts?.ok ?? 0} valid</Badge>
                          {entry.attributes?.counts?.unmatched > 0 && (
                            <Badge tone="warn">{entry.attributes.counts.unmatched} unmatched</Badge>
                          )}
                        </>
                      )}
                      <span className="xs muted" style={{ marginLeft: 'auto' }}>
                        {entry.error ? '' : formatMs(entry.latency_ms)}
                      </span>
                    </div>
                    <div className="compare-col__body">
                      {entry.error ? (
                        <div
                          className="small"
                          style={{ padding: '10px 13px', color: 'var(--danger)' }}
                        >
                          {entry.error}
                        </div>
                      ) : (
                        <>
                          <div className="xs subtle" style={{ padding: '4px 13px 8px' }}>
                            {entry.version.training?.metric_value !== null &&
                            entry.version.training?.metric_value !== undefined
                              ? `${entry.version.training.metric_name} ${formatMetric(entry.version.training.metric_value)} · epoch ${entry.version.training.epoch ?? '—'}`
                              : 'no training metrics recorded'}
                          </div>
                          {(entry.attributes?.predictions || [])
                            .filter(
                              (row) =>
                                !onlyDiffs ||
                                result.diff.find(
                                  (diff) => diff.attr_name === row.attr_name && diff.differs,
                                ),
                            )
                            .map((row) => (
                              <div
                                key={row.attr_name}
                                className={`diff-row ${
                                  result.diff.find(
                                    (diff) => diff.attr_name === row.attr_name && diff.differs,
                                  )
                                    ? 'diff-row--differs'
                                    : ''
                                }`}
                              >
                                <span className="diff-row__key" title={row.attr_name}>
                                  {row.attr_name}
                                </span>
                                <span className="diff-row__value">
                                  {row.state === 'missing' ? '—' : row.value}
                                </span>
                              </div>
                            ))}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {diffRows.length > 0 && (
                <div className="card" style={{ overflowX: 'auto' }}>
                  <table className="diff-table">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 220 }}>Attribute</th>
                        {result.results.map((entry) => (
                          <th key={entry.version.id}>{entry.version.id}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {diffRows.map((row) => (
                        <tr key={row.attr_name} className={row.differs ? 'differs' : ''}>
                          <td className="muted">{row.attr_name}</td>
                          {result.results.map((entry) => {
                            const cell = row.values[entry.version.id];
                            return (
                              <td key={entry.version.id}>
                                {cell?.state === 'missing' ? (
                                  <span className="subtle">—</span>
                                ) : (
                                  cell?.value || <span className="subtle">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {result.differing_count === 0 && (
                <EmptyState glyph="=" title="The versions agree">
                  Every compared attribute got the same value from all versions on this image.
                </EmptyState>
              )}
            </>
          )}
        </div>
      </main>
    </AppShell>
  );
}
